import { formatDate, formatUah, PROJECT_STATUSES, type ProjectStatus } from '@hr/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { FolderKanbanIcon, PlusIcon } from 'lucide-react';
import { useMemo } from 'react';
import { z } from 'zod';
import { Combobox } from '@/components/combobox';
import { DataTable } from '@/components/data-table';
import { EmptyState, PageHeader } from '@/components/page';
import { SearchInput } from '@/components/search-input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ProjectFormSheet } from '@/features/registry/project-form-sheet';
import { PROJECT_STATUS_STYLE } from '@/features/registry/status';
import { useRegistry } from '@/features/registry/use-registry';
import { PROJECT_STATUS_LABELS, uk } from '@/i18n/uk';
import { isHr } from '@/lib/roles';
import { listSearchShape, tableState, tableStateToSearch } from '@/lib/search-params';
import { trpc } from '@/lib/trpc';
import type { ProjectListItem } from '@/lib/types';

const searchSchema = z.object({
  ...listSearchShape,
  status: z.enum(PROJECT_STATUSES).optional().catch(undefined),
  donor: z.uuid().optional().catch(undefined),
  new: z.boolean().optional().catch(undefined),
});

export const Route = createFileRoute('/_app/projects/')({
  validateSearch: searchSchema,
  component: ProjectsPage,
});

function ProjectsPage() {
  const t = uk.registry;
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { me } = Route.useRouteContext();
  const canWrite = isHr(me.role);
  const registry = useRegistry();
  const state = tableState(search, { sort: 'startsAt', dir: 'desc' });
  const setSearch = (patch: Partial<z.infer<typeof searchSchema>>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  const list = useQuery({
    ...trpc.projects.list.queryOptions({
      page: state.page,
      pageSize: state.pageSize,
      sort: state.sort,
      dir: state.dir,
      q: search.q,
      status: search.status,
      donorId: search.donor,
    }),
    placeholderData: keepPreviousData,
  });

  const columns = useMemo<ColumnDef<ProjectListItem>[]>(
    () => [
      {
        id: 'code',
        header: t.code,
        cell: ({ row }) => <span className="font-medium">{row.original.code}</span>,
      },
      { id: 'name', header: t.name, accessorKey: 'name', meta: { className: 'min-w-56' } },
      {
        id: 'donor',
        header: t.donor,
        cell: ({ row }) => row.original.donorName ?? uk.common.empty,
      },
      {
        id: 'startsAt',
        header: t.period,
        cell: ({ row }) => (
          <span className="whitespace-nowrap">
            {formatDate(row.original.startsAt)} – {formatDate(row.original.endsAt) || '…'}
          </span>
        ),
      },
      {
        id: 'budget',
        header: t.budget,
        cell: ({ row }) => (
          <span className="whitespace-nowrap tabular-nums">{formatUah(row.original.budget)}</span>
        ),
        meta: { className: 'text-right' },
      },
      {
        id: 'staffCount',
        header: t.staffCount,
        enableSorting: false,
        cell: ({ row }) => row.original.staffCount,
        meta: { className: 'text-right' },
      },
      {
        id: 'status',
        header: t.status,
        cell: ({ row }) => (
          <Badge variant="outline" className={PROJECT_STATUS_STYLE[row.original.status]}>
            {PROJECT_STATUS_LABELS[row.original.status]}
          </Badge>
        ),
      },
    ],
    [t],
  );
  const hasFilters = Boolean(search.q || search.status || search.donor);

  return (
    <>
      <PageHeader
        title={t.projects}
        actions={
          canWrite && (
            <Button onClick={() => setSearch({ new: true })}>
              <PlusIcon /> {t.newProject}
            </Button>
          )
        }
      />
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <SearchInput
          value={search.q ?? ''}
          placeholder={`${t.code}, ${t.name.toLowerCase()}…`}
          onChange={(q) => setSearch({ q: q || undefined, page: undefined })}
        />
        <Select
          value={search.status ?? 'all'}
          onValueChange={(v) =>
            setSearch({ status: v === 'all' ? undefined : (v as ProjectStatus), page: undefined })
          }
        >
          <SelectTrigger className="w-40" aria-label={t.status}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {t.status}: {uk.common.all.toLowerCase()}
            </SelectItem>
            {PROJECT_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {PROJECT_STATUS_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Combobox
          className="w-48"
          value={search.donor}
          onChange={(v) => setSearch({ donor: v ?? undefined, page: undefined })}
          options={(registry.data?.donors ?? []).map((d) => ({ value: d.id, label: d.name }))}
          placeholder={t.donor}
          allowClear
        />
      </div>
      <DataTable
        columns={columns}
        data={list.data?.items}
        total={list.data?.total ?? 0}
        state={state}
        onStateChange={(next) => setSearch(tableStateToSearch(next))}
        isLoading={list.isPending}
        getRowId={(r) => r.id}
        onRowClick={(r) =>
          void navigate({ to: '/projects/$projectId', params: { projectId: r.id } })
        }
        empty={
          <EmptyState
            icon={FolderKanbanIcon}
            title={hasFilters ? uk.common.notFound : t.emptyProjects}
            hint={hasFilters ? undefined : t.emptyProjectsHint}
          />
        }
      />
      <ProjectFormSheet
        open={Boolean(search.new)}
        onOpenChange={(o) => setSearch({ new: o || undefined })}
      />
    </>
  );
}
